import { HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject } from '@angular/core';
import { Prettify, patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { SavedListSearchResult } from '@idealsupply/ngclient-webservice-shopping-cart';
import { AuthenticationStore } from 'authentication-data';
import { DialogService } from 'common-ui';
import { EntityRecord } from 'state-data';
import { SavedPartListStore } from '../data-stores/saved-part-list.store';

export const SavedPartListsStore = signalStore(
  { providedIn: 'root' },
  withState(() => ({
    _savedPartListRecord: undefined as EntityRecord<SavedListSearchResult> | undefined,
  })),
  withComputed((store) => {
    const savedPartListsLoading = computed(() => store._savedPartListRecord()?.loading ?? false);
    const savedPartListsError = computed(() => store._savedPartListRecord()?.error);
    const savedPartLists = computed(() => store._savedPartListRecord()?.entity?.data ?? []);

    return {
      savedPartListsLoading,
      savedPartListsError,
      savedPartLists,
    };
  }),
  withMethods(() => {
    const dialogService = inject(DialogService);
    const savedPartListStore = inject(SavedPartListStore);

    const saveListAs = async (name: string) => {
      try {
        return await savedPartListStore.saveListAs(name);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to save parts list: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const replaceSavedList = async (id: string) => {
      try {
        return await savedPartListStore.replaceSavedList(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to save parts list: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const mergeToSavedList = async (id: string) => {
      try {
        return await savedPartListStore.mergeToSavedList(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to merge parts list: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };

    const deleteSavedList = async (id: string) => {
      try {
        return await savedPartListStore.deleteSavedList(id);
      } catch (e) {
        if (e instanceof HttpErrorResponse) {
          dialogService.errorMessage(`Failed to delete parts list: ${e.message}`, 'Server Error');
        }
        throw e;
      }
    };
    return {
      saveListAs,
      replaceSavedList,
      mergeToSavedList,
      deleteSavedList,
    };
  }),
  withHooks((store) => {
    const savedPartListStore = inject(SavedPartListStore);
    const authStore = inject(AuthenticationStore);
    return {
      onInit() {
        effect(() => {
          const isAuthenticated = authStore.isAuthenticated();
          const record = isAuthenticated ? savedPartListStore.getAll()() : undefined;
          patchState(store, { _savedPartListRecord: record });
        });
      },
    };
  }),
);

export type SavedPartListsStore = Prettify<InstanceType<typeof SavedPartListsStore>>;
